// chatActions.js
import axios from '../helpers/axios';
import { chatConstants } from './constants';

const extractChats = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.chats)) return data.chats;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.data?.chats)) return data.data.chats;

    return [];
};

const extractErrorMessage = (error, fallback = 'Ошибка загрузки') => {
    return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback
    );
};

const buildQuery = (params = {}) => {
    const qs = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            qs.set(key, String(value));
        }
    });

    const query = qs.toString();

    return query ? `?${query}` : '';
};

const normalizeFetchMyChatsArgs = (arg = {}) => {
    if (typeof arg === 'string') {
        return {
            context: 'all',
            type: arg || 'all',
        };
    }

    return {
        context: arg.context || 'all',
        type: arg.type || 'all',
    };
};

export const toggleStar = (messageId, userId, on) => async () => {
    try {
        await axios.post(`/messages/${messageId}/star`, { userId, on });
    } catch (error) {
        console.warn('[STAR] toggle error', extractErrorMessage(error));
    }
};

export const fetchStarred = (chatId, { before, limit = 50 } = {}) => async () => {
    const { data } = await axios.get(`/chat/${chatId}/starred${buildQuery({ before, limit })}`);

    return data;
};

export const fetchChatMedia = (chatId, { type = 'image', before, limit = 60 } = {}) => async () => {
    const { data } = await axios.get(`/chat/${chatId}/media${buildQuery({ type, before, limit })}`);

    return data;
};

export const searchChat = (chatId, { q = '', dateFrom, dateTo, starred = false, before, limit = 50 } = {}) => async () => {
    const { data } = await axios.get(`/chat/${chatId}/timeline${buildQuery({
        q,
        dateFrom,
        dateTo,
        starred: starred ? 'true' : '',
        before,
        limit,
    })}`);

    return data;
};

export const fetchAllChatsForAdmin = ({ scope = '' } = {}) => {
    return async (dispatch) => {
        try {
            dispatch({ type: chatConstants.FETCH_CHATS_REQUEST });

            const res = await axios.get(`/admin/chats${buildQuery({ scope })}`);
            const chats = extractChats(res.data);

            dispatch({
                type: chatConstants.FETCH_CHATS_SUCCESS,
                payload: {
                    chats,
                    raw: res.data,
                },
            });

            return {
                ok: true,
                chats,
                raw: res.data,
            };
        } catch (error) {
            const message = extractErrorMessage(error, 'Не удалось загрузить админские чаты');

            dispatch({
                type: chatConstants.FETCH_CHATS_FAILURE,
                payload: {
                    error: message,
                    status: error?.response?.status || 0,
                    data: error?.response?.data || null,
                },
            });

            return {
                ok: false,
                error: message,
                status: error?.response?.status || 0,
                data: error?.response?.data || null,
            };
        }
    };
};

export const fetchMyChats = (arg = {}) => async (dispatch) => {
    const params = normalizeFetchMyChatsArgs(arg);

    try {
        dispatch({ type: chatConstants.FETCH_MY_CHATS_REQUEST });

        const res = await axios.get(`/chats/my${buildQuery(params)}`);
        const chats = extractChats(res.data);

        dispatch({
            type: chatConstants.FETCH_MY_CHATS_SUCCESS,
            payload: {
                chats,
                raw: res.data,
            },
        });

        return {
            ok: true,
            chats,
            raw: res.data,
        };
    } catch (error) {
        const message = extractErrorMessage(error, 'Не удалось загрузить мои чаты');

        dispatch({
            type: chatConstants.FETCH_MY_CHATS_FAILURE,
            payload: {
                error: message,
                status: error?.response?.status || 0,
                data: error?.response?.data || null,
            },
        });

        return {
            ok: false,
            error: message,
            status: error?.response?.status || 0,
            data: error?.response?.data || null,
        };
    }
};

export const createDirectChat = (peerId) => async (dispatch) => {
    try {
        dispatch({ type: chatConstants.CREATE_DM_REQUEST });

        const res = await axios.post(`/chats/dm/${peerId}`);
        const chat = res.data?.chat || res.data?.data?.chat || res.data;

        dispatch({
            type: chatConstants.CREATE_DM_SUCCESS,
            payload: { chat },
        });

        return {
            ok: true,
            payload: { chat },
        };
    } catch (error) {
        const message = extractErrorMessage(error, 'Не удалось создать личный чат');

        dispatch({
            type: chatConstants.CREATE_DM_FAILURE,
            payload: { error: message },
        });

        return {
            ok: false,
            error: message,
        };
    }
};

export const createRoomChat = ({ title, participants }) => async (dispatch) => {
    try {
        dispatch({ type: chatConstants.CREATE_ROOM_REQUEST });

        const res = await axios.post('/chats/room', { title, participants });
        const chat = res.data?.chat || res.data?.data?.chat || res.data;

        dispatch({
            type: chatConstants.CREATE_ROOM_SUCCESS,
            payload: { chat },
        });

        return {
            ok: true,
            payload: { chat },
        };
    } catch (error) {
        const message = extractErrorMessage(error, 'Не удалось создать групповой чат');

        dispatch({
            type: chatConstants.CREATE_ROOM_FAILURE,
            payload: { error: message },
        });

        return {
            ok: false,
            error: message,
        };
    }
};

export const fetchOfficeChats = (officeId, type = 'all') => async (dispatch) => {
    try {
        dispatch({ type: chatConstants.FETCH_MY_CHATS_REQUEST });

        const res = await axios.get(`/office/${officeId}/chats${buildQuery({ type })}`);
        const chats = extractChats(res.data);

        dispatch({
            type: chatConstants.FETCH_MY_CHATS_SUCCESS,
            payload: {
                chats,
                raw: res.data,
            },
        });

        return {
            ok: true,
            chats,
            raw: res.data,
        };
    } catch (error) {
        const message = extractErrorMessage(error, 'Не удалось загрузить офисные чаты');

        dispatch({
            type: chatConstants.FETCH_MY_CHATS_FAILURE,
            payload: {
                error: message,
                status: error?.response?.status || 0,
                data: error?.response?.data || null,
            },
        });

        return {
            ok: false,
            error: message,
        };
    }
};

export const addParticipantToRoom = (chatId, userId) => async () => {
    const { data } = await axios.post(`/chats/${chatId}/participants`, { userId });

    return data;
};

export const removeParticipantFromRoom = (chatId, userId) => async () => {
    const { data } = await axios.delete(`/chats/${chatId}/participants/${userId}`);

    return data;
};

export const setActiveChatIdLocal = (chatId) => ({
    type: chatConstants.SET_ACTIVE_CHAT_ID,
    payload: { chatId },
});


export const getOrCreateSavedChat = () => async (dispatch) => {
    try {
        dispatch({ type: chatConstants.CREATE_DM_REQUEST });
        const res = await axios.post('/chats/saved');
        const chat = res.data?.chat || res.data?.data?.chat || res.data;
        dispatch({ type: chatConstants.CREATE_DM_SUCCESS, payload: { chat } });
        return { ok: true, payload: { chat } };
    } catch (error) {
        const message = extractErrorMessage(error, 'Не удалось открыть сохранённые сообщения');
        dispatch({ type: chatConstants.CREATE_DM_FAILURE, payload: { error: message } });
        return { ok: false, error: message };
    }
};
